import { Fragment } from 'react';
import dayjs from 'dayjs';
import { Tooltip } from 'antd';
import { Button, Table } from 'components/common';
import styled from 'styled-components';
import { StatusPip } from '../styled';
import { ENRICHMENT_TYPE_LABELS, StatusPipConfig } from '../helpers';

const ActionButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  margin-left: auto;

  .hidden-cell-row & {
    margin-left: 0;
  }
`;

export const ActionButton = styled(Button).attrs(p => ({
  style: {
    width: `${p.autoWidth ? 'auto' : '130px'}`,
  },
}))`
  height: 25px;
`;

export const tablePerPage = [
  { value: 10, label: '10 per page' },
  { value: 25, label: '25 per page' },
  { value: 50, label: '50 per page' },
  { value: 100, label: '100 per page' },
];

export const EnrichmentTable = styled(Table)`
  max-height: calc(100vh - 400px);
  overflow-y: auto;
`;

const toolTipTitles = (status, listId) => {
  if (status === 0) {
    return 'Enrichment is still processing';
  } else if (status === 1) {
    return listId ? '' : 'List is not available';
  } else if (status === 2) {
    return 'Enrichment failed';
  }
};

const DataEnrichmentTable = ({
  user,
  data,
  isLoading,
  updateFilters,
  filters,
  handleViewReport,
  handleViewList,
}) => {
  const headers = [
    {
      label: 'File Name',
      key: 'filename',
      minWidth: 120,
      maxContentWidth: 180,
    },
    {
      label: 'Date Uploaded',
      key: 'createdAt',
      minWidth: 80,
    },
    {
      label: 'Type',
      key: 'type',
      minWidth: 100,
    },
    {
      label: 'Status',
      key: 'status',
      minWidth: 100,
    },
    {
      label: 'Total Rows',
      key: 'totalRows',
      minWidth: 50,
    },
    {
      label: 'Matched Rows',
      key: 'matchedRows',
      minWidth: 50,
    },
    {
      minWidth: 160,
    },
  ];

  if (user.isUserManager) {
    headers.splice(-1, 0, {
      label: 'User',
      key: 'userName',
      minWidth: 100,
      maxContentWidth: 180,
    });
  }

  let config = {
    headers: headers,
    createRow: (x, i) => {
      const statusConfig = StatusPipConfig[x.status] || {
        text: 'Unknown',
        color: '#6c757d',
        background: '#e9ecef',
      };

      return (
        <Fragment key={`${x.id}`}>
          <div
            title={x.filename}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {x.filename}
          </div>

          <div>{dayjs(x.createdAt).format('Do MMM YYYY')}</div>
          <div>{ENRICHMENT_TYPE_LABELS[x.type]}</div>
          <Tooltip title={statusConfig.tooltip} placement='right'>
            <StatusPip
              color={statusConfig.color}
              background={statusConfig.background}
            >
              {statusConfig.text}
            </StatusPip>
          </Tooltip>
          <div>{x.totalRows}</div>
          <div>{x.matchedRows}</div>
          {user.isUserManager && <div>{x.userName}</div>}
          <ActionButtonsWrapper>
            <ActionButton
              size='small'
              type='secondary'
              onClick={() =>
                handleViewReport(
                  x.id,
                  x.status === 0,
                  ENRICHMENT_TYPE_LABELS[x.type]?.toLowerCase()
                )
              }
            >
              View Summary
            </ActionButton>
            <Tooltip title={toolTipTitles(x.status, x.listId)}>
              <ActionButton
                size='small'
                type='primary'
                disabled={x.status !== 1 || x.listId == null}
                onClick={() => handleViewList(x.listId, x.type)}
              >
                {x.status === 0 ? 'Pending' : 'View List'}
              </ActionButton>
            </Tooltip>
          </ActionButtonsWrapper>
        </Fragment>
      );
    },
  };

  function setPageSize(pageSize) {
    updateFilters({ ...filters, pageSize });
  }

  function setPageNumber(pageNumber) {
    updateFilters({ ...filters, pageNumber });
  }

  function handleSortChange(orderBy) {
    let order;
    if (orderBy !== filters.orderBy) {
      order = 'asc';
    } else {
      order = filters.order === 'desc' ? 'asc' : 'desc';
    }

    if (
      filters.orderBy !== 'createdAt' &&
      filters.orderBy === orderBy &&
      filters.order === 'desc'
    ) {
      orderBy = 'createdAt';
      order = 'desc';
    }

    updateFilters({ ...filters, orderBy, order });
  }

  return (
    <EnrichmentTable
      config={config}
      data={data?.data ?? []}
      loading={isLoading}
      pageSizeOptions={tablePerPage}
      pageSize={filters.pageSize ?? 10}
      pageNumber={filters.pageNumber ?? 0}
      resultSize={data?.total ?? 0}
      onSortChange={handleSortChange}
      sort={filters.orderBy}
      sortDir={filters.order}
      onChangePageSize={setPageSize}
      onChangePageNumber={setPageNumber}
      paginate
      sortable
    />
  );
};

export default DataEnrichmentTable;
